// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartDataParser.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
const SEPARATOR_SIGN = ';#';

import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { isObservable, toJS } from 'mobx';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import { MappingValues } from 'logic/data/Izenda.ChartDefaultConfig';
const { isObject } = require('lodash/fp');

import {
  get,
  getOr,
  isString,
  forEach,
  first,
  uniq,
  isNil,
  remove,
  find,
  last,
  map,
  sortBy,
  set,
  groupBy,
  zipWith,
  assign
} from 'lodash/fp';

export default {
  parser,
  getSerieProppertiesByField,
  getValueStyle,
  processDataByBottomPercent,
  getTextColor,
  getCellColor,
  getName,
  getPartNames
};

/**
 * build parser for chart
 * @param  {object} containers
 * @param  {number} expandedLevel
 * @param  {object} previewData
 * @param  {object} dataStructure
 * @return {object}
 */
function parser(containers: any, expandedLevel: number, previewData: any, dataStructure: any): any {
  const fieldsMapping = previewData.fieldsMapping;
  let records = previewData.records || {};
  const fieldContainers = containers;
  const fields = isObservable(fieldsMapping) ? toJS(fieldsMapping) : fieldsMapping;

  dataStructure = dataStructure || ProcessDataUtils.restructureField(fieldContainers, fields);

  return {
    expandedLevel: expandedLevel || 0,
    dataStructure: dataStructure,
    fieldsMapping: fields,
    records: records,
    cities: get('cities', previewData),
    countryCodes: get('countryCodes', previewData),
    postalCodes: get('postalCodes', previewData),
    stateCodes: get('stateCodes', previewData),
    countyCodes: get('countyCodes', previewData),
    addressCodes: get('addressCodes', previewData),
    rawData: previewData.rawData,
    containers: fieldContainers,

    groupData: groupData,

    hasSeparator: function(separatorKey) {
      return dataStructure[separatorKey] && !!dataStructure[separatorKey].length;
    },

    getSeparatorInfo: function(separatorKey) {
      return dataStructure[separatorKey] && dataStructure[separatorKey][0];
    },

    getConfigDataByRange: ReportPartUtils.getConfigDataByRange,
    getFormatData: ReportPartUtils.getDataFormat,

    getConfigField: function(value, fieldMapping, callbacks) {
      const fieldElement = getOr({}, 'reportPartElm', fieldMapping) || {};
      const configField = {};

      // Config field for sub-report
      if (fieldElement.hasSubReport) {
        configField.className = configField.className ? [configField.className, 'u_textLink'].join(' ') : 'u_textLink';
        configField.onClick = function() {
          callbacks.onOpenSubReport && callbacks.onOpenSubReport(value);
        };
      }

      return configField;
    },

    getDataByField: function (fieldInfo, source, fieldConfig) {
      if (!fieldInfo) {
        return {};
      }

      return {
        name: fieldInfo.fieldNameAlias,
        data: ProcessDataUtils.getListValueByField(fieldInfo, source || records, fieldConfig)
      };
    },

    getSerieName: function(name) {
      name = (name && name.split(SEPARATOR_SIGN)) || [];

      return {
        name: name.length > 1 ? name[1] : name[0],
        other: name[0]
      };
    },
    getValueStyle: getValueStyle,
    setRecord: function(newRecords) {
      records = newRecords;
    }
  };

  /**
   * Group data based on separatorKey, first element of component will be used to group data
   * @param  {string | object} container-key or field-info
   * @param  {Array<object>} groupNames
   * @param  {Array<object>} groupRecords
   * @return {object}
   */
  function groupData(groupInfo: string | any, groupNames: Array<any>, groupRecords: Array<any>) {
    const source = groupRecords || records;
    const names = [];
    let columnName, groupedData;

    // groupInfo is container-name: group by first-field of container
    groupInfo = isString(groupInfo) ? first(dataStructure[groupInfo]) : groupInfo;
    columnName = get('columnName', groupInfo);

    groupedData = columnName ? groupData(columnName, source) : source;
    forEach(name => {
      groupNames.push(name);
    }, uniq(names));

    return groupedData;

    function groupData(columnName, source) {
      let groupName;
      return groupBy(record => {
        groupName = record[columnName];

        names && names.push(groupName);
        return groupName;
      }, source);
    }
  }
}

/**
 * get style value
 * @param {object} val
 * @param {object} config
 * @param {object} fieldSource
 */
function getValueStyle(val: any, config: any, fieldSource: any) {
  config = config || {};
  const originalValue = isObject(val) ? getOr(null, 'originalValue', val) : val;
  const valueField =  isObject(val) ? val : {value: val};
  // On axis label formatting callbacks, fieldSource will be undefined
  return {
    fomattedValue: ReportPartUtils.getDataFormat(valueField, config, fieldSource),
    valueWithoutAltText: ReportPartUtils.getDataFormat(valueField, config, fieldSource, false),
    style: {
      color: getTextColor(originalValue, config, fieldSource),
      cellColor: getCellColor(originalValue, config, fieldSource)
    }
  };
}

/**
 * get text color value
 * @param {object} val
 * @param {object} config
 * @param {object} fieldSource
 */
function getTextColor(val: any, config: any, fieldSource: any) {
  const textColor = get('textColor', config);
  return !isNil(val) && !isNil(textColor)
    ? ReportPartUtils.getConfigDataByRange(val, textColor, fieldSource)
    : undefined;
}

/**
 * get cell color value
 * @param {object} val
 * @param {object} config
 * @param {object} fieldSource
 */
function getCellColor(val: any, config: any, fieldSource: any) {
  const cellColor = get('cellColor', config);
  return !isNil(val) && !isNil(cellColor) && !isNil(fieldSource.record)
    ? ReportPartUtils.getConfigDataByRange(val, cellColor, fieldSource)
    : undefined;
}

/**
 * read properties of fields and return property which need to apply to chart
 * @param  {object} field
 * @return {object}
 */
function getSerieProppertiesByField(field: any): any {
  field = field || {};

  const serieName = field.fieldName;

  if (!serieName) {
    return {};
  }

  const seriesProps = field.properties || {};
  const getValueByKey = seriesProps.getValueByKey && seriesProps.getValueByKey.bind(seriesProps);
  const otherProps = seriesProps.otherProps || {};
  const alternativeText = getValueByKey && getValueByKey('alternativeText');
  const subReport = (getValueByKey && getValueByKey('subReport')) || {};
  const customURL = getValueByKey && getValueByKey('customURL');
  const embeddedJavascript = getValueByKey && getValueByKey('embeddedJavascript');

  const textCellColor = getValueByKey && getValueByKey('color');
  const textColor = getOr({}, 'textColor', textCellColor);
  const cellColor = getOr({}, 'cellColor', textCellColor);

  return {
    fieldElement: field,
    fieldDataType: field.previewDataType,
    fieldFormatData: field.fieldFormatData,
    numberOfPointActions: field.numberOfPointActions,
    alternativeText: {
      rangePercent: alternativeText.rangePercent,
      rangeValue: alternativeText.rangeValue,
      value: alternativeText.value
    },

    textColors: textColor,
    textColor: {
      rangePercent: textColor.rangePercent,
      rangeValue: textColor.rangeValue,
      value: textColor.value
    },

    cellColors: cellColor,
    cellColor: {
      rangePercent: cellColor.rangePercent,
      rangeValue: cellColor.rangeValue,
      value: cellColor.value
    },
    customURL: customURL.url ? customURL : null,
    embeddedJavascript: embeddedJavascript.script ? embeddedJavascript : null,

    chartOptions: {
      chartType: otherProps.chartType,
      yAxisName: otherProps.yAxis,
      izLineDashStyle: otherProps.dashStyle,
      izLineWidth: otherProps.lineWidth
    },

      subReport: subReport.selectedReport ? subReport : null,
      showTotal: otherProps.showTotal, // sparkline
      axisOption: parseAxisOption(field, otherProps.axis),
      threshold: parseThresholdOption(field, otherProps.threshold),
      dynamicThresholds: parseThresholdOption(field, otherProps.dynamicThresholds),
      regression: parseRegressionOption(otherProps.regression)
    };
  }

/**
 * Clean data with bottom percent
 * @param {number} percent
 * @param {Array<object>} data
 * @param {string} nameField
 * @param {string} valueField
 */
function processDataByBottomPercent(percent: number, data: Array<any>, nameField: string, valueField: string): any {
  let sum = 0;
  let val;

  const sortedData = sortBy(item => {
    val = get(valueField, item);
    sum += val;
    return val;
  }, data);

  const totalBottomValue = ((percent * sum) / 100).toFixed(2);
  let counting = 0;
  let index = 0;
  const otherItems = [];
  const otherPercentItems = [];
  let otherItem;

  // find items which need to replaced by other-item
  while (counting < totalBottomValue && index < sortedData.length) {
    otherItems.push(sortedData[index]);
    otherPercentItems.push({ 'percentage': get(valueField, sortedData[index]) * 100 / sum });
    counting += get(valueField, sortedData[index]);
    index++;
  }

  if (counting > totalBottomValue) {
    counting -= get(valueField, otherItems.pop());
    otherPercentItems.pop();
  }

  // do not have data for other-group
  if (!otherItems.length) {
    return {
      otherItems: null,
      otherItem: null,
      newData: data
    };
  }

  // other item info
  otherItem = set(nameField, 'Others', otherItem);
  otherItem = set(valueField, counting, otherItem);
  otherItem = set(valueField, counting, otherItem);
  otherItem.isComputed = true;
  otherItem.includedItems = zipWith((i, j) => assign(i, j), otherPercentItems, otherItems);

  // remove item which is in other-group
  const newData = remove(
    item => !!find(otherItem => get(nameField, item) === get(nameField, otherItem), otherItems),
    data
  );

  newData.push(otherItem);

  return {
    otherItems: otherItems,
    otherItem: otherItem,
    newData: newData
  };
}

/**
 * Get name's parts
 * @param {object} field
 */
function getPartNames(field: any): Array<string> {
  const fileNameAlias = getOr('', 'fieldNameAlias', field);
  return fileNameAlias.split(SEPARATOR_SIGN);
}

/**
 * Get name of field
 * @param {object} field
 */
function getName(field: any): string {
  return last(getPartNames(field));
}

/**
 * Format value of Threshold for Data Time data type
 * @param {object} field
 * @param {object} thresholds
 */
function parseThresholdOption(field: any, thresholds: any): any {
  const result = toJS(thresholds);
  if (field.previewDataType === DATA_TYPES.DATE_TIME) {
    map(threshold => {
      threshold.izValue = convertToDateTimeFormat(field.fieldFormatData, threshold.izValue, false);
    }, result);
  }

  return result;
}

/**
 * Format value of Axis option for Data Time data type
 * @param {object} field
 * @param {object} axisOption
 */
function parseAxisOption(field: any, axisOption: any): any {
  const result = toJS(axisOption);

  if (field.previewDataType === DATA_TYPES.DATE_TIME) {
    !!get('izStartingPoint', result) &&
      (result.izStartingPoint = Date.parse(
        convertToDateTimeFormat(field.fieldFormatData, get('izStartingPoint', result), false)
      ));
  }

  return result;
}

/**
 * Parse regression option value
 * @param {object} regression
 */
function parseRegressionOption(regression: any): any {
  if (isNil(regression)) return regression;
  const result = toJS(regression);
  result.dashStyle = MappingValues.LINE_STYLE_VALUES[regression.dashStyle];
  return result;
}



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.ChartDataParser.js